import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from './AuthContext';

const ListContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useListContext = () => {
  const context = useContext(ListContext);
  if (!context) {
    throw new Error('useListContext must be used within a ListProvider');
  }
  return context;
};

export const ListProvider = ({ children }) => {
  const { toast } = useToast();
  const { token, user } = useAuth();
  const saveTimeoutRef = useRef(null);
  const loadedRef = useRef(false);
  const isGuest = !token;
  const GUEST_KEY = "guest_grocery_lists";

  // Guest lists persist across browser sessions via localStorage so shoppers
  // never lose their list. Cleared on signup after promoting into backend.
  const readGuestLists = () => {
    try {
      const raw = localStorage.getItem(GUEST_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch { return null; }
  };

  // Initialize with empty default list — session load will overwrite for returning users
  const [lists, setLists] = useState(() => {
    const guest = typeof window !== "undefined" ? readGuestLists() : null;
    return guest || [
      {
        id: 1,
        name: "My Grocery List",
        color: "green",
        items: [],
        budget: 150,
        budgetType: "monthly",
        createdAt: new Date().toISOString(),
        shared: false
      }
    ];
  });

  const [currentListId, setCurrentListId] = useState(() => {
    const guest = typeof window !== "undefined" ? readGuestLists() : null;
    return guest ? guest[0].id : 1;
  });

  // Load session from backend on mount
  useEffect(() => {
    if (isGuest) {
      // Nothing to fetch — localStorage hydration already ran in useState
      loadedRef.current = true;
      return;
    }
    if (loadedRef.current) return;
    const load = async () => {
      // If this user just signed up from guest mode, PROMOTE the localStorage
      // list into their backend session so nothing is lost.
      const guestLists = readGuestLists();
      try {
        if (guestLists && guestLists.length && guestLists[0]?.items?.length) {
          await fetch(`${BACKEND_URL}/api/auth/session`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ lists: guestLists }),
          });
          setLists(guestLists);
          setCurrentListId(guestLists[0].id);
          try { localStorage.removeItem(GUEST_KEY); } catch { /* ignore */ }
          loadedRef.current = true;
          toast?.({ title: "Your grocery list is saved to your account." });
          return;
        }
        const res = await fetch(`${BACKEND_URL}/api/auth/session`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.lists && data.lists.length > 0) {
            setLists(data.lists);
            setCurrentListId(data.lists[0].id);
          }
        }
      } catch (e) { /* use defaults */ }
      loadedRef.current = true;
    };
    load();
  }, [token, isGuest]);

  // Guest → persist to localStorage. Signed-in → debounced backend save.
  const saveSession = useCallback(async (listsToSave) => {
    if (isGuest) {
      try {
        localStorage.setItem(GUEST_KEY, JSON.stringify(listsToSave));
      } catch { /* quota / private mode — silently drop */ }
      return;
    }
    if (!token || !loadedRef.current) return;
    try {
      await fetch(`${BACKEND_URL}/api/auth/session`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lists: listsToSave }),
      });
    } catch (e) { /* silent */ }
  }, [token, isGuest]);

  useEffect(() => {
    if (!loadedRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveSession(lists), 2000);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [lists, saveSession]);

  // Force-save now (called on logout)
  const saveNow = useCallback(async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    await saveSession(lists);
  }, [lists, saveSession]);

  const currentList = lists.find(list => list.id === currentListId);

  // List Management Functions
  const handleSelectList = (listId) => {
    setCurrentListId(listId);
  };

  const handleCreateList = (newList) => {
    setLists([...lists, newList]);
    setCurrentListId(newList.id);
    toast({
      title: "List Created!",
      description: `"${newList.name}" has been created successfully.`,
    });
  };

  const handleUpdateList = (listId, updates) => {
    setLists(lists.map(list =>
      list.id === listId
        ? { ...list, ...updates }
        : list
    ));
    toast({
      title: "List Updated!",
      description: "Your changes have been saved.",
    });
  };

  const handleDeleteList = (listId) => {
    if (lists.length <= 1) return;

    const remainingLists = lists.filter(list => list.id !== listId);
    setLists(remainingLists);

    if (currentListId === listId) {
      setCurrentListId(remainingLists[0].id);
    }

    toast({
      title: "List Deleted",
      description: "The list has been removed.",
    });
  };

  const handleShareList = (listId) => {
    const list = lists.find(l => l.id === listId);
    navigator.clipboard.writeText(`Check out my grocery list: "${list.name}" - ${list.items.length} items`);

    setLists(lists.map(l =>
      l.id === listId
        ? { ...l, shared: true }
        : l
    ));

    toast({
      title: "List Shared!",
      description: "Share link copied to clipboard.",
    });
  };

  // Update items in current list
  const updateCurrentList = (newItems) => {
    setLists(lists.map(list =>
      list.id === currentListId
        ? { ...list, items: newItems }
        : list
    ));
  };

  // Add items to a specific list (for multi-list AI support)
  const addItemsToList = (listId, itemsToAdd) => {
    setLists(lists.map(list =>
      list.id === listId
        ? { ...list, items: [...itemsToAdd, ...list.items] }
        : list
    ));
  };

  // Find list by name (fuzzy matching for AI)
  const findListByName = (searchName) => {
    if (!searchName) return currentList;

    const searchLower = searchName.toLowerCase();

    let foundList = lists.find(list =>
      list.name.toLowerCase() === searchLower
    );

    if (!foundList) {
      foundList = lists.find(list =>
        list.name.toLowerCase().includes(searchLower) ||
        searchLower.includes(list.name.toLowerCase())
      );
    }

    if (!foundList) {
      foundList = lists.find(list => {
        const listWords = list.name.toLowerCase().split(' ');
        const searchWords = searchLower.split(' ');
        return listWords.some(word => searchWords.includes(word)) ||
               searchWords.some(word => listWords.includes(word));
      });
    }

    return foundList || currentList;
  };

  // Replace a generic grocery item with a specific deal product
  const replaceItemWithDeal = (genericName, dealProduct) => {
    const list = lists.find(l => l.id === currentListId);
    if (!list) return;

    const newItems = list.items.map(item => {
      if (item.name.toLowerCase() === genericName.toLowerCase() && !item.completed) {
        return {
          ...item,
          name: dealProduct.name,
          original_name: genericName,
          estimated_price: dealProduct.price,
          image_url: dealProduct.image_url || "",
          store: dealProduct.store || "",
          deal_selected: true,
        };
      }
      return item;
    });

    setLists(lists.map(l => l.id === currentListId ? { ...l, items: newItems } : l));
    toast({
      title: "Item Updated!",
      description: `Replaced "${genericName}" with "${dealProduct.name}" from ${dealProduct.store}.`,
    });
  };

  // Batch replace multiple grocery items at once (for Smart Auto-Pick)
  const batchReplaceItemsWithDeals = (replacements) => {
    const list = lists.find(l => l.id === currentListId);
    if (!list || replacements.length === 0) return;

    const replacementMap = {};
    for (const r of replacements) {
      replacementMap[r.genericName.toLowerCase()] = r.dealProduct;
    }

    const newItems = list.items.map(item => {
      const deal = replacementMap[item.name.toLowerCase()];
      if (deal && !item.completed) {
        return {
          ...item,
          name: deal.name,
          original_name: item.name,
          estimated_price: deal.price,
          image_url: deal.image_url || "",
          store: deal.store || "",
          deal_selected: true,
        };
      }
      return item;
    });

    setLists(lists.map(l => l.id === currentListId ? { ...l, items: newItems } : l));
    toast({
      title: "Smart Auto-Pick Complete!",
      description: `Updated ${replacements.length} items with the cheapest deals.`,
    });
  };

  const contextValue = {
    lists,
    setLists,
    currentListId,
    setCurrentListId,
    currentList,
    handleSelectList,
    handleCreateList,
    handleUpdateList,
    handleDeleteList,
    handleShareList,
    updateCurrentList,
    addItemsToList,
    findListByName,
    replaceItemWithDeal,
    batchReplaceItemsWithDeals,
    saveNow
  };

  return (
    <ListContext.Provider value={contextValue}>
      {children}
    </ListContext.Provider>
  );
};
